'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useTranslation } from '@/hooks/useTranslation';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SearchSuggestion {
  id: number;
  text: string;
  category: string;
  icon: string;
}

const SearchModal = ({ isOpen, onClose }: SearchModalProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, isHydrated]);

  if (!isHydrated) {
    return null;
  }

  if (!isOpen) return null;

  const suggestions: SearchSuggestion[] = [
    { id: 1, text: 'CNC Machining Services', category: 'Product', icon: 'CogIcon' },
    { id: 2, text: 'PCB Assembly Manufacturers', category: 'Manufacturer', icon: 'BuildingOfficeIcon' },
    { id: 3, text: 'Injection Molding', category: 'Product', icon: 'CubeIcon' },
    { id: 4, text: 'ISO 9001 Certified Suppliers', category: 'Certification', icon: 'ShieldCheckIcon' },
    { id: 5, text: 'Metal Fabrication', category: 'Category', icon: 'WrenchScrewdriverIcon' },
  ];

  const recentSearches = [
    'Yarn & Spinning Manufacturers',
    'Electronic Components',
    'Industrial Machinery',
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4">
      <div
        className="absolute inset-0 bg-foreground/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-3xl bg-card rounded-lg shadow-modal border border-border">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Icon name="MagnifyingGlassIcon" size={24} variant="outline" className="text-muted-foreground" />
            <input
              type="text"
              placeholder={t('searchModalPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 text-lg bg-transparent border-none outline-none text-foreground placeholder:text-muted-foreground"
              autoFocus
            />
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-md transition-colors duration-200"
            >
              <Icon name="XMarkIcon" size={24} variant="outline" className="text-muted-foreground" />
            </button>
          </div>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {searchQuery.length === 0 && (
            <>
              <div className="p-6 border-b border-border">
                <h3 className="text-sm font-semibold text-muted-foreground mb-4">{t('recentSearches')}</h3>
                <div className="space-y-2">
                  {recentSearches.map((search, index) => (
                    <button
                      key={index}
                      className="flex items-center gap-3 w-full px-4 py-3 rounded-md hover:bg-muted transition-colors duration-200 text-left"
                    >
                      <Icon name="ClockIcon" size={18} variant="outline" className="text-muted-foreground" />
                      <span className="text-sm text-foreground">{search}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-sm font-semibold text-muted-foreground mb-4">{t('popularSearches')}</h3>
                <div className="space-y-2">
                  {suggestions.map((suggestion) => (
                    <button
                      key={suggestion.id}
                      className="flex items-center gap-3 w-full px-4 py-3 rounded-md hover:bg-muted transition-colors duration-200 text-left"
                    >
                      <Icon name={suggestion.icon as any} size={18} variant="outline" className="text-primary" />
                      <div className="flex-1">
                        <span className="text-sm text-foreground">{suggestion.text}</span>
                        <span className="text-xs text-muted-foreground ml-2">· {suggestion.category}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {searchQuery.length > 0 && (
            <div className="p-6">
              <p className="text-sm text-muted-foreground text-center py-8">
                Start typing to see search results...
              </p>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-1 bg-background border border-border rounded text-xs">↵</kbd>
                to search
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-1 bg-background border border-border rounded text-xs">ESC</kbd>
                to close
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;